import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import {
    signUp,
    signIn,
    confirmSignUp,
    resendSignUpCode,
} from "../../common/auth";
import { readLandingLead, splitLandingNombre, hydrateLandingLeadFromSearch } from "../../common/landingLead";
import { trackEvent } from "../../hooks/useTrackEvent";
import TermsPanel from "../../components/Legal/TermsPanel";
import {
  acceptLegalDocumentOnboarding,
  TERMS_DOCUMENT_ID,
} from "../../lib/legalGraphql";
import "../../components/Legal/TermsPanel.css";

function slugify(text) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 48);
}

function persistProfile(form) {
  sessionStorage.setItem("onboarding_password", form.password);
  sessionStorage.setItem(
    "onboarding_profile",
    JSON.stringify({
      nombre: form.nombre,
      apellido: form.apellido,
      email: form.email,
    })
  );
}

async function completeVerification(email, password, navigate, termsVersion) {
  await signIn(email, password);
  if (termsVersion) {
    await acceptLegalDocumentOnboarding(termsVersion, TERMS_DOCUMENT_ID);
    sessionStorage.setItem("onboarding_terms_accepted_version", String(termsVersion));
  }
  sessionStorage.setItem("onboarding_email_confirmed", "true");
  trackEvent("onboarding_step_1_account_created", {
    event_category: "onboarding",
    event_label: "cognito_signup",
    step_number: 1,
    user_email: email,
  });
  navigate("/onboarding/workspace");
}

export default function StepCuenta() {
  const navigate = useNavigate();
  const location = useLocation();
  const [phase, setPhase] = useState("register");
  const [form, setForm] = useState({ nombre: "", apellido: "", email: "", password: "" });
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [termsVersion, setTermsVersion] = useState(null);
  const [scrollComplete, setScrollComplete] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);

  const handleTermsReady = useCallback((meta) => {
    if (meta?.current_published_version) {
      setTermsVersion(meta.current_published_version);
    }
  }, []);

  useEffect(() => {
    hydrateLandingLeadFromSearch(location.search);
    if (sessionStorage.getItem("onboarding_email_confirmed") === "true") return;
    try {
      const saved = JSON.parse(sessionStorage.getItem("onboarding_profile") || "{}");
      if (saved.email) {
        setForm((f) => ({
          ...f,
          nombre: saved.nombre || "",
          apellido: saved.apellido || "",
          email: saved.email,
          password: sessionStorage.getItem("onboarding_password") || "",
        }));
        setPhase("verify");
        setInfo(`Confirmá tu email con el código enviado a ${saved.email}`);
        return;
      }
      const lead = readLandingLead();
      if (lead.email) {
        const { nombre, apellido } = splitLandingNombre(lead.nombre);
        setForm((f) => ({
          ...f,
          nombre: nombre || f.nombre,
          apellido: apellido || f.apellido,
          email: lead.email,
        }));
      }
    } catch {
      /* ignore */
    }
  }, [location.search]);

  const canContinueRegister =
    scrollComplete && termsChecked && termsVersion && !loading;

  const handleRegister = async () => {
    setLoading(true);
    setError("");
    setInfo("");
    try {
      if (!canContinueRegister) {
        throw new Error("Leé y aceptá los Términos y Condiciones para continuar");
      }
      if (!form.nombre?.trim() || !form.apellido?.trim()) {
        throw new Error("Completá nombre y apellido");
      }
      if (!form.email || !form.password || form.password.length < 8) {
        throw new Error("Completá email y contraseña (mín. 8 caracteres)");
      }
      const result = await signUp(form.email, form.password, {
        given_name: form.nombre.trim(),
        family_name: form.apellido.trim(),
      });
      persistProfile(form);
      sessionStorage.setItem("onboarding_terms_pending_version", String(termsVersion));

      if (result?.userConfirmed) {
        await completeVerification(form.email, form.password, navigate, termsVersion);
        return;
      }

      setPhase("verify");
      setInfo(`Enviamos un código de verificación a ${form.email}`);
    } catch (e) {
      if (e.code === "UsernameExistsException") {
        persistProfile(form);
        setPhase("verify");
        setInfo(
          "Ya existe una cuenta con ese email. Ingresá el código de verificación o iniciá sesión."
        );
        return;
      }
      setError(e.message || "No se pudo crear la cuenta");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    setLoading(true);
    setError("");
    setInfo("");
    try {
      if (!code.trim()) throw new Error("Ingresá el código de verificación");
      const password =
        form.password || sessionStorage.getItem("onboarding_password");
      if (!form.email) throw new Error("Falta el email — volvé a completar el formulario");

      const pendingVersion =
        termsVersion ||
        Number(sessionStorage.getItem("onboarding_terms_pending_version") || 0);
      if (!pendingVersion) {
        throw new Error("Debés aceptar los Términos y Condiciones antes de continuar");
      }

      await confirmSignUp(form.email, code.trim());
      await completeVerification(form.email, password, navigate, pendingVersion);
    } catch (e) {
      setError(e.message || "Código inválido o expirado");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    setError("");
    try {
      await resendSignUpCode(form.email);
      setInfo("Código reenviado. Revisá tu bandeja de entrada.");
    } catch (e) {
      setError(e.message || "No se pudo reenviar el código");
    } finally {
      setLoading(false);
    }
  };

  if (phase === "verify") {
    return (
      <>
        <div className="ob-card-header">
          <div className="ob-step-icon">
            <MarkEmailReadOutlinedIcon sx={{ fontSize: 24 }} />
          </div>
          <div className="ob-card-header-text">
            <h2 className="ob-step-title">Verificá tu email</h2>
            <p className="ob-step-desc">
              Ingresá el código que enviamos a <strong>{form.email}</strong>
            </p>
          </div>
        </div>

        {info && <p className="ob-info">{info}</p>}

        <div className="ob-field">
          <label>Código de verificación</label>
          <input
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </div>

        {error && <p className="ob-error">{error}</p>}

        <button
          className="ob-btn-primary"
          onClick={handleVerify}
          disabled={loading}
          type="button"
        >
          {loading ? "Verificando..." : "Verificar y continuar →"}
        </button>
        <button
          className="ob-btn-secondary"
          onClick={handleResend}
          disabled={loading}
          type="button"
        >
          Reenviar código
        </button>
        <button
          className="ob-btn-secondary"
          onClick={() => {
            setPhase("register");
            setCode("");
            setError("");
          }}
          type="button"
        >
          ← Cambiar email
        </button>
      </>
    );
  }

  return (
    <>
      <div className="ob-card-header">
        <div className="ob-step-icon">
          <PersonOutlineIcon sx={{ fontSize: 24 }} />
        </div>
        <div className="ob-card-header-text">
          <h2 className="ob-step-title">Creá tu cuenta</h2>
          <p className="ob-step-desc">Empezá gratis. Sin tarjeta de crédito.</p>
        </div>
      </div>

      <div className="ob-field-row">
        <div className="ob-field">
          <label>Nombre</label>
          <input
            placeholder="Juan"
            value={form.nombre}
            onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
          />
        </div>
        <div className="ob-field">
          <label>Apellido</label>
          <input
            placeholder="García"
            value={form.apellido}
            onChange={(e) => setForm((f) => ({ ...f, apellido: e.target.value }))}
          />
        </div>
      </div>
      <div className="ob-field">
        <label>Email</label>
        <input
          type="email"
          placeholder="juan@empresa.com"
          value={form.email}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
        />
      </div>
      <div className="ob-field">
        <label>Contraseña</label>
        <input
          type="password"
          placeholder="Mínimo 8 caracteres"
          value={form.password}
          onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
        />
      </div>

      <TermsPanel
        version={termsVersion}
        onReady={handleTermsReady}
        onScrollComplete={setScrollComplete}
      />

      <label className="terms-check-row">
        <input
          type="checkbox"
          checked={termsChecked}
          disabled={!scrollComplete}
          onChange={(e) => setTermsChecked(e.target.checked)}
        />
        <span>He leído y acepto los Términos y Condiciones</span>
      </label>

      {error && <p className="ob-error">{error}</p>}

      <button
        className="ob-btn-primary"
        onClick={handleRegister}
        disabled={!canContinueRegister}
        type="button"
      >
        {loading ? "Creando cuenta..." : "Continuar →"}
      </button>
    </>
  );
}

export { slugify };
